import { useMemo } from "react";
import { Card } from "@tremor/react";
import { Tooltip } from "antd";

import { cn } from "@fitness-app/utils/src/styles/cn";

import { type HeatmapCell } from "../hooks/useChatResponseMetrics";

interface ConversationsHeatmapProps {
  data: HeatmapCell[];
}

const DAYS = ["Pon", "Wt", "Śr", "Czw", "Pt", "Sob", "Niedz"];
const HOURS = [
  "00:00",
  "02:00",
  "04:00",
  "06:00",
  "08:00",
  "10:00",
  "12:00",
  "14:00",
  "16:00",
  "18:00",
  "20:00",
  "22:00",
];

const getColorIntensity = (count: number, maxCount: number): string => {
  if (count === 0 || maxCount === 0) return "bg-gray-100";
  const ratio = count / maxCount;
  if (ratio < 0.2) return "bg-blue-100";
  if (ratio < 0.4) return "bg-blue-200";
  if (ratio < 0.6) return "bg-blue-300";
  if (ratio < 0.8) return "bg-blue-400";
  return "bg-blue-500";
};

export const ConversationsHeatmap = ({ data }: ConversationsHeatmapProps) => {
  const { grid, maxCount } = useMemo(() => {
    const gridData: number[][] = Array.from({ length: 12 }, () => Array(7).fill(0) as number[]);
    let max = 0;

    data.forEach((cell) => {
      const hourIndex = Math.floor(cell.hour / 2);
      if (gridData[hourIndex]) {
        gridData[hourIndex][cell.dayOfWeek] = (gridData[hourIndex][cell.dayOfWeek] ?? 0) + cell.count;
        const currentValue = gridData[hourIndex][cell.dayOfWeek] ?? 0;
        if (currentValue > max) max = currentValue;
      }
    });

    return { grid: gridData, maxCount: max };
  }, [data]);

  const hasData = data.length > 0;

  return (
    <Card>
      <h3 className="text-tremor-content-strong dark:text-dark-tremor-content-strong font-medium">
        Wiadomości od klientów według dnia i godziny
      </h3>
      <p className="text-tremor-content dark:text-dark-tremor-content mt-1 text-sm">
        Najedź na komórkę, aby zobaczyć szczegóły
      </p>

      {!hasData ? (
        <p className="text-tremor-content dark:text-dark-tremor-content mt-4">Brak danych do wyświetlenia</p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <div className="min-w-[500px]">
            {/* Header row with days */}
            <div className="mb-1 flex">
              <div className="w-14" />
              {DAYS.map((day) => (
                <div key={day} className="flex-1 text-center text-xs text-gray-500">
                  {day}
                </div>
              ))}
            </div>

            {/* Grid rows */}
            {grid.map((row, hourIndex) => (
              <div key={hourIndex} className="mb-1 flex items-center">
                <div className="w-14 pr-2 text-right text-xs text-gray-500">{HOURS[hourIndex]}</div>
                {row.map((count, dayIndex) => (
                  <Tooltip
                    key={`${hourIndex}-${dayIndex}`}
                    title={`${DAYS[dayIndex]}, ${HOURS[hourIndex]}: ${count} wiadomości`}
                  >
                    <div
                      className={cn(
                        "mx-0.5 h-6 flex-1 cursor-pointer rounded-sm transition-all hover:ring-2 hover:ring-blue-400",
                        getColorIntensity(count, maxCount),
                      )}
                    />
                  </Tooltip>
                ))}
              </div>
            ))}

            {/* Legend */}
            <div className="mt-4 flex items-center justify-center gap-2">
              <span className="text-xs text-gray-500">0</span>
              <div className="flex gap-0.5">
                <div className="h-4 w-4 rounded-sm bg-gray-100" />
                <div className="h-4 w-4 rounded-sm bg-blue-100" />
                <div className="h-4 w-4 rounded-sm bg-blue-200" />
                <div className="h-4 w-4 rounded-sm bg-blue-300" />
                <div className="h-4 w-4 rounded-sm bg-blue-400" />
                <div className="h-4 w-4 rounded-sm bg-blue-500" />
              </div>
              <span className="text-xs text-gray-500">{maxCount}</span>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};
